import React from 'react';
import { NavLink } from 'react-router-dom';
import { Users, Shield, LogOut, FileText, BarChart3, ClipboardCheck, Briefcase, Settings, Filter, MessageSquare, Brain, Workflow, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import { useTenant } from '../contexts/TenantContext';
import useAuthStore from '../store/authStore';
import PropelLogo from './PropelLogo';

export function AdminSidebar({ onLogout, isCollapsed = true, onToggle }) {
  const {
    canManageUsers,
    canManageRoles,
    canReadAuditLog,
    canReadAnalytics,
    canReviewProposals,
    canApproveUpworkActions,
    canReadPortfolio,
    canReadVectorData,
    canReadTenantConfig,
    canUpdateTenantConfig,
    canReadJobFilters,
    canUpdateJobFilters,
  } = usePermissions();
  const { user } = useAuthStore();
  const { tenant } = useTenant();
  const workflowMode = tenant?.settings?.workflowMode || 'hybrid';
  const showReviewSection = workflowMode !== 'auto_approve';

  // Group navigation items by category
  const navGroups = [
    {
      title: 'Team',
      items: [
        { path: 'users', label: 'Users', icon: Users, permission: canManageUsers },
        { path: 'roles', label: 'Roles', icon: Shield, permission: canManageRoles },
      ]
    },
    // Workflow group — only when review is part of the workflow
    ...(showReviewSection ? [{
      title: 'Workflow',
      items: [
        { path: 'proposals', label: 'Proposal Reviews', icon: ClipboardCheck, permission: canReviewProposals },
        { path: 'workflow', label: 'Review Settings', icon: Workflow, permission: canUpdateTenantConfig },
      ]
    }] : []),
    {
      title: 'AI & Data',
      items: [
        { path: 'vector-data', label: 'AI Knowledge', icon: Brain, permission: canReadVectorData },
        { path: 'portfolio', label: 'Portfolio', icon: Briefcase, permission: canReadPortfolio },
      ]
    },
    {
      title: 'Insights',
      items: [
        { path: 'analytics', label: 'Analytics', icon: BarChart3, permission: canReadAnalytics },
        { path: 'audit-logs', label: 'Audit Logs', icon: FileText, permission: canReadAuditLog },
      ]
    },
  ];

  const adminOnlyItems = [
    { path: 'approvals', label: 'Upwork Approvals', icon: ClipboardCheck, permission: canApproveUpworkActions },
    { path: 'integrations', label: 'Integrations', icon: Settings, permission: () => canReadTenantConfig() || canUpdateTenantConfig() },
    { path: 'job-filters', label: 'Job Filters', icon: Filter, permission: () => canReadJobFilters() || canUpdateJobFilters() },
    { path: 'message-filters', label: 'Message Filters', icon: MessageSquare, permission: canUpdateTenantConfig },
  ];

  const collapsedLinkClass = (isActive) =>
    `group relative flex items-center justify-center w-10 h-10 rounded-xl transition-colors ${
      isActive
        ? 'bg-primary-50 text-primary-700 ring-1 ring-primary-100'
        : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
    }`;

  const collapsedIconClass = (isActive) =>
    `h-5 w-5 flex-shrink-0 ${isActive ? 'text-primary-600' : 'text-gray-500'}`;

  const expandedLinkClass = ({ isActive }) =>
    `w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      isActive
        ? 'bg-primary-50 text-primary-700'
        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
    }`;

  const expandedIconClass = (isActive) =>
    `h-4 w-4 flex-shrink-0 ${isActive ? 'text-primary-600' : 'text-gray-400'}`;

  if (isCollapsed) {
    return (
      <aside className="w-14 bg-white border-r border-gray-200 flex flex-col h-full">
        {/* Brand Header */}
        <div className="h-14 flex items-center justify-center border-b border-gray-200 relative">
          <NavLink to="/admin/users" className="group relative flex items-center justify-center" title="Propel Admin">
            <div className="w-9 h-9 bg-white border border-primary-200 rounded-xl flex items-center justify-center shadow-sm">
              <PropelLogo className="w-5 h-5" />
            </div>
            <span className="sr-only">Propel Admin</span>
          </NavLink>
          <button
            type="button"
            onClick={onToggle}
            className="absolute -right-4 top-1/2 -translate-y-1/2 z-50 w-7 h-7 rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm hover:text-gray-700 hover:shadow transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            aria-label="Expand sidebar"
          >
            <ChevronsRight className="w-3.5 h-3.5 mx-auto" />
          </button>
        </div>

        {/* User Info */}
        <div className="py-3 flex items-center justify-center border-b border-gray-100">
          <div
            className="w-9 h-9 bg-primary-100 rounded-full flex items-center justify-center text-xs font-semibold text-primary-700"
            title={`${user?.name || 'User'} • ${user?.role || 'Role'}`}
          >
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-3 flex flex-col items-center gap-3">
          {navGroups.map((group, index) => {
            const visibleItems = group.items.filter(item => item.permission());
            if (visibleItems.length === 0) return null;

            return (
              <div key={group.title} className="flex flex-col items-center gap-2">
                {index > 0 && <div className="w-7 h-px bg-gray-200 my-1" />}
                {visibleItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.path}
                      to={`/admin/${item.path}`}
                      className={({ isActive }) => collapsedLinkClass(isActive)}
                      title={item.label}
                      aria-label={item.label}
                    >
                      {({ isActive }) => (
                        <>
                          <Icon className={collapsedIconClass(isActive)} />
                          <span className="sr-only">{item.label}</span>
                          <span className="pointer-events-none absolute left-12 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                            {item.label}
                          </span>
                        </>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}

          {/* Admin-only Settings Section */}
          {adminOnlyItems.some(item => item.permission()) && (
            <div className="flex flex-col items-center gap-2">
              <div className="w-7 h-px bg-gray-200 my-1" />
              {adminOnlyItems.filter(item => item.permission()).map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={`/admin/${item.path}`}
                    className={({ isActive }) => collapsedLinkClass(isActive)}
                    title={item.label}
                    aria-label={item.label}
                  >
                    {({ isActive }) => (
                      <>
                        <Icon className={collapsedIconClass(isActive)} />
                        <span className="sr-only">{item.label}</span>
                        <span className="pointer-events-none absolute left-12 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                          {item.label}
                        </span>
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          )}
        </nav>

        {/* Footer with Logout */}
        <div className="py-2 border-t border-gray-200 flex items-center justify-center">
          <button
            type="button"
            onClick={onLogout}
            className="group relative flex items-center justify-center w-10 h-10 rounded-xl text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors"
            title="Sign out of admin panel"
            aria-label="Sign out"
          >
            <LogOut className="h-5 w-5" />
            <span className="pointer-events-none absolute left-12 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
              Sign Out
            </span>
          </button>
        </div>
      </aside>
    );
  }

  return (
    <aside className="w-60 bg-white border-r border-gray-200 flex flex-col h-full">
      {/* Brand Header */}
      <div className="h-14 flex items-center justify-between px-4 border-b border-gray-200">
        <NavLink to="/admin/users" className="flex items-center space-x-2.5 group">
          <div className="w-9 h-9 bg-white border border-primary-200 rounded-xl flex items-center justify-center shadow-sm">
            <PropelLogo className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-sm font-bold text-gray-900 group-hover:text-primary-600 transition-colors">Propel</h1>
            <p className="text-[10px] text-gray-500">Admin Console</p>
          </div>
        </NavLink>
        <button
          type="button"
          onClick={onToggle}
          className="w-8 h-8 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          aria-label="Collapse sidebar"
        >
          <ChevronsLeft className="w-4 h-4 mx-auto" />
        </button>
      </div>

      {/* User Info */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div className="w-9 h-9 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-medium text-primary-700">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-gray-900 truncate">{user?.name || 'User'}</p>
            <p className="text-[10px] text-gray-500 truncate capitalize">{user?.role || 'Role'}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-4">
        {navGroups.map((group) => {
          const visibleItems = group.items.filter(item => item.permission());
          if (visibleItems.length === 0) return null;

          return (
            <div key={group.title}>
              <h3 className="px-2.5 mb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                {group.title}
              </h3>
              <div className="space-y-0.5">
                {visibleItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.path}
                      to={`/admin/${item.path}`}
                      className={expandedLinkClass}
                    >
                      {({ isActive }) => (
                        <>
                          <Icon className={expandedIconClass(isActive)} />
                          <span className="truncate">{item.label}</span>
                          {isActive && (
                            <div className="ml-auto w-1.5 h-1.5 rounded-full bg-primary-600 flex-shrink-0" />
                          )}
                        </>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Admin-only Settings Section */}
        {adminOnlyItems.some(item => item.permission()) && (
          <div>
            <h3 className="px-2.5 mb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
              Settings
            </h3>
            <div className="space-y-0.5">
              {adminOnlyItems.filter(item => item.permission()).map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={`/admin/${item.path}`}
                    className={expandedLinkClass}
                  >
                    {({ isActive }) => (
                      <>
                        <Icon className={expandedIconClass(isActive)} />
                        <span className="truncate">{item.label}</span>
                        {isActive && (
                          <div className="ml-auto w-1.5 h-1.5 rounded-full bg-primary-600 flex-shrink-0" />
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>
        )}
      </nav>

      {/* Footer with Logout */}
      <div className="p-3 border-t border-gray-200">
        <button
          type="button"
          onClick={onLogout}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors"
          title="Sign out of admin panel"
        >
          <LogOut className="h-4 w-4" />
          Sign Out
        </button>
      </div>
    </aside>
  );
}
